import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { handleRejectedResponse } from './utils';

// Async Thunks

// Catalog Analyzer fee settings (Shipping / Warehouse) — seller-side inputs, not pulled from any
// external source. Scoped to the account owner server-side, so the owner and every employee under
// them read/write the same one setting rather than each having their own.
// scope: 'global' (default, omit params entirely) | 'catalog' (pass catalogId) | 'database'.
export const getCatalogFeeSettings = createAsyncThunk(
  'catalog/get-fee-settings',
  async ({ scope, catalogId } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (scope) params.scope = scope;
      if (catalogId) params.catalogId = catalogId;
      const response = await axios.get(`${process.env.OPULENT_NODE_API_URL}/users/catalog-fee-settings`, { params });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Catalog Fee Settings', rejectWithValue);
    }
  }
);

// settings: any subset of { shipping_fee, warehouse_fee, profit_mode }, plus scope/catalog_id
// when saving a catalog- or database-scope override (global is the default when omitted).
export const saveCatalogFeeSettings = createAsyncThunk(
  'catalog/save-fee-settings',
  async (settings, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${process.env.OPULENT_NODE_API_URL}/users/catalog-fee-settings`, settings);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Save Catalog Fee Settings', rejectWithValue);
    }
  }
);

// Clears a catalog- or database-scope override, reverting that scope to inheriting global.
export const clearCatalogFeeSettings = createAsyncThunk(
  'catalog/clear-fee-settings',
  async ({ scope, catalogId }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${process.env.OPULENT_NODE_API_URL}/users/catalog-fee-settings`, {
        data: { scope, catalog_id: catalogId }
      });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Clear Catalog Fee Settings', rejectWithValue);
    }
  }
);

// Every Fee Settings row for this account (global + every catalog/database override) in one
// call - feeds the consolidated "All Catalogs" view's per-row fee resolution.
export const getCatalogFeeSettingsOverrides = createAsyncThunk(
  'catalog/get-fee-settings-overrides',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.OPULENT_NODE_API_URL}/users/catalog-fee-settings/overrides`);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Catalog Fee Settings Overrides', rejectWithValue);
    }
  }
);

// Progress for the background Recompute_FBM_Profit run a Fee Settings save/clear triggered -
// scoped identically to the settings themselves (global/catalog/database).
export const getFbmRecomputeStatus = createAsyncThunk(
  'catalog/get-fbm-recompute-status',
  async ({ scope, catalogId } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (scope) params.scope = scope;
      if (catalogId) params.catalogId = catalogId;
      const response = await axios.get(`${process.env.OPULENT_NODE_API_URL}/users/catalog-fee-settings/recompute-status`, { params });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get FBM Recompute Status', rejectWithValue);
    }
  }
);

export const getCatalogs = createAsyncThunk(
  'catalog/get-catalogs',
  async ({ 
    sortField = 'id', 
    sortOrder = 'desc',
    searchTerm = '',
    supplierId,
    page,
    limit
  } = {}, { rejectWithValue }) => {
    try {
      const params = {
        sortField,
        sortOrder,
        search: searchTerm || undefined, // Only send if not empty
        page,
        limit
      };
      
      // Only add supplier if provided
      if (supplierId) {
        params.supplier = supplierId;
      }
      
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/catalog/get-catalogs`,
        { params }
      );
      return response.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Catalogs', rejectWithValue);
    }
  }
);

export const uploadCatalog = createAsyncThunk(
  'catalog/upload-catalog',
  async (catalogData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/catalog/upload-catalog`,
        catalogData
      );
      return response.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Upload Catalog', rejectWithValue, true, true);
    }
  }
);

export const deleteCatalog = createAsyncThunk('catalog/delete-catalog', async (catalogId, { rejectWithValue }) => {
  try {
    const response = await axios.patch(
      `${process.env.OPULENT_NODE_API_URL}/catalog/delete-catalog`,
      {
        catalogId
      }
    );
    return response.data;
  } catch (err) {
    return handleRejectedResponse(err, 'Delete Catalog', rejectWithValue);
  }
});

export const getCatalogItems = createAsyncThunk(
  'catalog/get-catalog-items',
  async ({catalogId, filters, page, limit}, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/catalog/get-catalog-items/${catalogId}`,
        {
          params: {
            filters,
            page,
            limit
          }
        }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Catalog Items', rejectWithValue);
    }
  }
);

export const exportCatalogItems = createAsyncThunk(
  'catalog/export-catalog-items',
  async ({catalogId, filters}, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/catalog/export-catalog-items/${catalogId}`,
        filters,
        { 
          responseType: 'blob',
          timeout: 0, // allow long-running streaming downloads
          // Enable streaming - data will arrive progressively
          onDownloadProgress: (progressEvent) => {
            // Optional: Can track progress here if needed
            const percentCompleted = progressEvent.total 
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            // Progress tracking can be added here if needed
          }
        }
      );
      
      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'export.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Return blob and filename - download will be handled in component
      return { blob: response.data, filename };
    } catch (err) {
      return handleRejectedResponse(err, 'Export Catalog Items', rejectWithValue, true, true);
    }
  }
);

export const getCatalogProcessStatus = createAsyncThunk(
  'catalog/catalog-process-status',
  async (catalogIds, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/catalog/catalog-process-status`,
        {
          params: {
            catalogIds
          }
        }
      );
      return response.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Catalog Status', rejectWithValue);
    }
  }
);

export const updateBundleQuantity = createAsyncThunk(
  'catalog/update-bundle-quantity',
  async ({ catalog_item_id, bundle_quantity }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.OPULENT_NODE_API_URL}/catalog/update-bundle-qty`,
        {
          catalogItemId: catalog_item_id,
          bundleQuantity: bundle_quantity
        }
      );
      return response.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Update Bundle Quanitity', rejectWithValue);
    }
  }
);

export const saveInDatabase = createAsyncThunk('catalog/save-in-database', async (catalogProductData, { rejectWithValue }) => {
  try {
    const response = await axios.post(
      `${process.env.OPULENT_NODE_API_URL}/catalog/save-in-database`,
      catalogProductData
    );
    return response.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Save Database Product', rejectWithValue, false);
  }
});

export const getCatalogHistory = createAsyncThunk('catalog/get-catalog-history', async (upc, { rejectWithValue }) => {
  try {
    const response = await axios.get(
      `${process.env.OPULENT_NODE_API_URL}/catalog/get-catalog-history`,
      {
        params: {
          upc
        }
      }
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Get Catalog History', rejectWithValue);
  }
});

export const getPoHistory = createAsyncThunk('catalog/get-po-history', async (upc, { rejectWithValue }) => {
  try {
    const response = await axios.get(
      `${process.env.OPULENT_NODE_API_URL}/catalog/get-po-history`,
      {
        params: {
          upc
        }
      }
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Get PO History', rejectWithValue);
  }
});

export const getSkuListForCatalog = createAsyncThunk('catalog/get-sku-list', async ({asin, upc}, { rejectWithValue }) => {
  try {
    const response = await axios.get(
      `${process.env.OPULENT_NODE_API_URL}/catalog/get-sku-list`,
      {
        params: {
          asin,
          upc
        }
      }
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Get SKU List For Catalog', rejectWithValue);
  }
});

export const validateMsku = createAsyncThunk(
  'catalog/validate-msku',
  async ({ sku }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/catalog/validate-sku-asin`,
        { params: { sku } }
      );
      return response.data; // { isValid: true/false, message: '...' }
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Error validating MSKU';
      return rejectWithValue({ message });
    }
  }
);

export const updateProductNotes = createAsyncThunk(
  'catalog/update-notes',
  async ({ catalogId, productId, notes }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.OPULENT_NODE_API_URL}/catalog/update-notes`,
        { catalogId, productId, notes }
      );
      return response.data; // expected: updated product or { success: true }
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Error updating notes';
      return rejectWithValue({ message });
    }
  }
);


export const getPoOptions = createAsyncThunk('catalog/get-po-options', async ({ upc, vendor_id }, { rejectWithValue }) => {
  try {
    const response = await axios.get(
      `${process.env.OPULENT_NODE_API_URL}/catalog/get-po-options`,
      {
        params: { upc, vendor_id }
      }
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Get PO Options', rejectWithValue);
  }
});

export const addCatalogProductInPo = createAsyncThunk('catalog/add-catalog-product-in-po', async (data, { rejectWithValue }) => {
  try {
    const response = await axios.post(
      `${process.env.OPULENT_NODE_API_URL}/catalog/add-catalog-product-in-po`,
      data
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Add Catalog Product in PO', rejectWithValue, true, true);
  }
});

export const deleteProductMatch = createAsyncThunk('catalog/delete-product-match', async (catalogItemId, { rejectWithValue }) => {
  try {
    const response = await axios.put(
      `${process.env.OPULENT_NODE_API_URL}/catalog/delete-product-match`,
      {
        catalogItemId
      }
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Delete Product Match', rejectWithValue);
  }
});

// Database page "Delete Match": un-saves the (upc, asin) pairing — soft-deletes it from
// product_catalog_v2 and releases the saved-claim on its catalog items, which otherwise stay exactly
// as they were (still matched). Distinct from deleteProductMatch (per catalog item reject).
export const deleteDatabaseMatch = createAsyncThunk('catalog/delete-database-match', async ({ upc, asin }, { rejectWithValue }) => {
  try {
    const response = await axios.post(
      `${process.env.OPULENT_NODE_API_URL}/catalog/delete-database-match`,
      {
        upc,
        asin
      }
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Delete Match', rejectWithValue);
  }
});

export const getDatabaseProducts = createAsyncThunk(
  'catalog/get-database-products',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/catalog/get-database-products`,
        { params: filters }
      );
      return {
        data: response.data.data,
        total: response.data.total,
        overallTotal: response.data.overallTotal
      };
    } catch (err) {
      return handleRejectedResponse(err, 'Get Database Products', rejectWithValue);
    }
  }
);

export const exportDatabaseProducts = createAsyncThunk('catalog/export-database-products', async (filters, { rejectWithValue }) => {
  try {
    const response = await axios.get(
      `${process.env.OPULENT_NODE_API_URL}/catalog/export-database-products`,
      { 
        params: filters,
        responseType: 'blob',
        timeout: 0, // allow long-running streaming downloads
        // Enable streaming - data will arrive progressively
        onDownloadProgress: (progressEvent) => {
          // Optional: Can track progress here if needed
          const percentCompleted = progressEvent.total 
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          // Progress tracking can be added here if needed
        }
      }
    );
    
    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'export.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    // Return blob and filename - download will be handled in component
    return { blob: response.data, filename };
  }
  catch (err) {
    return handleRejectedResponse(err, 'Export Database Product', rejectWithValue, true, true);
  }
});

export const deleteDatabaseProduct = createAsyncThunk('catalog/delete-database-product', async (productId, { rejectWithValue }) => {
  try {
    const response = await axios.patch(
      `${process.env.OPULENT_NODE_API_URL}/catalog/delete-database-product`,
      { productId }
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Delete Database Product', rejectWithValue);
  }
});

export const manualUpcAsinMatching = createAsyncThunk('/manual-upc-asin', async (data, { rejectWithValue }) => {
  try {
    const response = await axios.post(
      `${process.env.CATALOG_ANALYZER_API_URL}/manual-upc-asin`,
      data
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Manual Upc Asin Matching', rejectWithValue, true, true);
  }
});

export const searchManualMatch = createAsyncThunk('catalog/upc-asin-search', async ({upc, asin, catalog_id}, { rejectWithValue }) => {
  try {
    const response = await axios.get(
      `${process.env.CATALOG_ANALYZER_API_URL}/upc-asin-search`,
      {
        params: {
          upc,
          asin,
          catalog_id
        }
      }
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Search Manual Match', rejectWithValue, true, true);
  }
});

export const getAsinHistory = createAsyncThunk('catalog/get-asin-history', async (asin, { rejectWithValue }) => {
  try {
    const response = await axios.get(
      `${process.env.OPULENT_NODE_API_URL}/catalog/get-asin-history`,
      {
        params: {
          asin
        }
      }
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Get ASIN History', rejectWithValue);
  }
});

export const updateSellPrice = createAsyncThunk('catalog/update-sell-price', async ({ catalog_item_id, sell_price }, { rejectWithValue }) => {
  try {
    const response = await axios.patch(
      `${process.env.CATALOG_ANALYZER_API_URL}/update-sell-price`,
      {
        catalog_item_id,
        sell_price
      }
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Update Sell Price', rejectWithValue, true, true);
  }
});

export const saveCatalogFilter = createAsyncThunk('catalog/save-filter', async (data, { rejectWithValue }) => {
  try {
    const response = await axios.post(
      `${process.env.OPULENT_NODE_API_URL}/catalog/save-filter`,
      data
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Save Catalog Filter', rejectWithValue);
  }
});

export const getCatalogFilters = createAsyncThunk('catalog/get-filters', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get(
      `${process.env.OPULENT_NODE_API_URL}/catalog/get-filters`
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Get Catalog Filters', rejectWithValue);
  }
});

export const deleteCatalogFilter = createAsyncThunk('catalog/delete-filter', async (id, { rejectWithValue }) => {
  try {
    const response = await axios.delete(
      `${process.env.OPULENT_NODE_API_URL}/catalog/delete-filter/${id}`
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Delete Catalog Filter', rejectWithValue);
  }
});

export const saveActivePreference = createAsyncThunk('catalog/save-active-preference', async (data, { rejectWithValue }) => {
  try {
    const response = await axios.post(
      `${process.env.OPULENT_NODE_API_URL}/users/active-preference`,
      data
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Save Active Preference', rejectWithValue);
  }
});

export const getActivePreference = createAsyncThunk('catalog/get-active-preference', async (context_key, { rejectWithValue }) => {
  try {
    const response = await axios.get(
      `${process.env.OPULENT_NODE_API_URL}/users/active-preference`,
      { params: { context_key } }
    );
    return response?.data;
  }
  catch (err) {
    return handleRejectedResponse(err, 'Get Active Preference', rejectWithValue);
  }
});

export const checkAsinGatedStatus = createAsyncThunk(
  'catalog/check-asin-gated-status',
  async ({ asin }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/catalog/check-asin-gated-status`,
        { params: { asin } }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Check ASIN Gated Status', rejectWithValue);
    }
  }
);

export const getAsinGatedCount = createAsyncThunk(
  'catalog/asin-gated-count',
  async ({ context, catalogId } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/catalog/asin-gated-count`,
        { params: { context, catalogId } }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get ASIN Gated Count', rejectWithValue);
    }
  }
);

export const runAllAsinGatedCheck = createAsyncThunk(
  'catalog/run-all-asin-gated-check',
  async ({ context, catalogId } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/catalog/run-all-asin-gated-check`,
        { context, catalogId }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Run All ASIN Gated Check', rejectWithValue);
    }
  }
);

export const getGatedCheckSession = createAsyncThunk(
  'catalog/get-gated-check-session',
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/catalog/gated-check-session/${sessionId}`
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Gated Check Session', rejectWithValue);
    }
  }
);


export const refreshBuyBoxPrice = createAsyncThunk(
  'catalog/refresh-buy-box',
  async ({ asins }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/catalog/refresh-buy-box`,
        { asins }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Refresh Buy Box Price', rejectWithValue);
    }
  }
);


// Slice
const catalog = createSlice({
  name: 'catalog',
  initialState: {
    fetchingCatalogs: false,
    fetchingCatalogItems: false,
    uploadingCatalog: false,
    deletingCatalog: false,
    exportingCatalogItems: false,
    exportingCatalogId: null,
    fetchingCatalogProcessStatus: false,
    fetchingCatalogHistory: false,
    fetchingPoHistory: false,
    fetchingSkuListForCatalog: false,
    validatingMsku: false,
    fetchingAsinHistory: false,
    fetchingPoOptions: false,
    addingCatalogProductInPo: false,
    fetchingDatabaseProducts: false,
    exportingDatabaseProducts: false,
    savingInDatabase: false,
    manualUpcAsinMatching: false,
    deletingMatch: false,
    searchingManualMatch: false,
    updatingSellPrice: false,
    refreshingBuyBox: false,
    checkingAsinGatedStatus: false,
    asinGatedCount: { willCheck: 0, willSkip: 0, total: 0 },
    fetchingAsinGatedCount: false,
    runningAllAsinGatedCheck: false,
    gatedCheckSessions: {}, // Map of context_catalogId -> session
    fetchingGatedCheckSession: false,
    catalogs: [],
    error: null
  },
  reducers: {
    setCatalogStates(state, { payload: { field, value } }) {
      state[field] = value;
    }
  },
  extraReducers: {
    [getCatalogs.pending]: (state) => {
      state.fetchingCatalogs = true;
    },
    [getCatalogs.fulfilled]: (state, action) => {
      state.fetchingCatalogs = false;
    },
    [getCatalogs.rejected]: (state) => {
      state.fetchingCatalogs = false;
    },
    [uploadCatalog.pending]: (state) => {
      state.uploadingCatalog = true;
    },
    [uploadCatalog.fulfilled]: (state, action) => {
      state.uploadingCatalog = false;
    },
    [uploadCatalog.rejected]: (state) => {
      state.uploadingCatalog = false;
    },
    [deleteCatalog.pending]: (state) => {
      state.deletingCatalog = true;
    },
    [deleteCatalog.fulfilled]: (state, action) => {
      state.deletingCatalog = false;
    },
    [deleteCatalog.rejected]: (state) => {
      state.deletingCatalog = false;
    },
    [exportCatalogItems.pending]: (state, action) => {
      state.exportingCatalogItems = true;
      state.exportingCatalogId = action.meta?.arg?.catalogId ?? null;
    },
    [exportCatalogItems.fulfilled]: (state) => {
      state.exportingCatalogItems = false;
      state.exportingCatalogId = null;
    },
    [exportCatalogItems.rejected]: (state) => {
      state.exportingCatalogItems = false;
      state.exportingCatalogId = null;
    },
    [getCatalogItems.pending]: (state) => {
      state.fetchingCatalogItems = true;
    },
    [getCatalogItems.fulfilled]: (state, action) => {
      state.fetchingCatalogItems = false;
    },
    [getCatalogItems.rejected]: (state) => {
      state.fetchingCatalogItems = false;
    },
    [getCatalogProcessStatus.pending]: (state) => {
      state.fetchingCatalogProcessStatus = true;
    },
    [getCatalogProcessStatus.fulfilled]: (state, action) => {
      state.fetchingCatalogProcessStatus = false;
    },
    [getCatalogProcessStatus.rejected]: (state) => {
      state.fetchingCatalogProcessStatus = false;
    },
    [getCatalogHistory.pending]: (state) => {
      state.fetchingCatalogHistory = true;
    },
    [getCatalogHistory.fulfilled]: (state, action) => {
      state.fetchingCatalogHistory = false;
    },
    [getCatalogHistory.rejected]: (state) => {
      state.fetchingCatalogHistory = false;
    },
    [getPoHistory.pending]: (state) => {
      state.fetchingPoHistory = true;
    },
    [getPoHistory.fulfilled]: (state, action) => {
      state.fetchingPoHistory = false;
    },
    [getPoHistory.rejected]: (state) => {
      state.fetchingPoHistory = false;
    },
    [getSkuListForCatalog.pending]: (state) => {
      state.fetchingSkuListForCatalog = true;
    },
    [getSkuListForCatalog.fulfilled]: (state, action) => {
      state.fetchingSkuListForCatalog = false;
    },
    [getSkuListForCatalog.rejected]: (state) => {
      state.fetchingSkuListForCatalog = false;
    },
    [getAsinHistory.pending]: (state) => {
      state.fetchingAsinHistory = true;
    },
    [getAsinHistory.fulfilled]: (state, action) => {
      state.fetchingAsinHistory = false;
    },
    [getAsinHistory.rejected]: (state) => {
      state.fetchingAsinHistory = false;
    },
    [getPoOptions.pending]: (state) => {
      state.fetchingPoOptions = true;
    },
    [getPoOptions.fulfilled]: (state, action) => {
      state.fetchingPoOptions = false;
    },
    [getPoOptions.rejected]: (state) => {
      state.fetchingPoOptions = false;
    },
    [addCatalogProductInPo.pending]: (state) => {
      state.addingCatalogProductInPo = true;
    },
    [addCatalogProductInPo.fulfilled]: (state, action) => {
      state.addingCatalogProductInPo = false;
    },
    [addCatalogProductInPo.rejected]: (state) => {
      state.addingCatalogProductInPo = false;
    },
    [getDatabaseProducts.pending]: (state) => {
      state.fetchingDatabaseProducts = true;
    },
    [getDatabaseProducts.fulfilled]: (state, action) => {
      state.fetchingDatabaseProducts = false;
      state.databaseProducts = action.payload.data;
      state.databaseTotal = action.payload.total;
      state.databaseOverallTotal = action.payload.overallTotal;
    },
    [getDatabaseProducts.rejected]: (state) => {
      state.fetchingDatabaseProducts = false;
    },
    [exportDatabaseProducts.pending]: (state) => {
      state.exportingDatabaseProducts = true;
    },
    [exportDatabaseProducts.fulfilled]: (state, action) => {
      state.exportingDatabaseProducts = false;
    },
    [exportDatabaseProducts.rejected]: (state) => {
      state.exportingDatabaseProducts = false;
    },
    [saveInDatabase.pending]: (state) => {
      state.savingInDatabase = true;
    },
    [saveInDatabase.fulfilled]: (state, action) => {
      state.savingInDatabase = false;
    },
    [saveInDatabase.rejected]: (state) => {
      state.savingInDatabase = false;
    },
    [manualUpcAsinMatching.pending]: (state) => {
      state.manualUpcAsinMatching = true;
    },
    [manualUpcAsinMatching.fulfilled]: (state, action) => {
      state.manualUpcAsinMatching = false;
    },
    [manualUpcAsinMatching.rejected]: (state) => {
      state.manualUpcAsinMatching = false;
    },
    [deleteProductMatch.pending]: (state) => {
      state.deletingMatch = true;
    },
    [deleteProductMatch.fulfilled]: (state, action) => {
      state.deletingMatch = false;
    },
    [deleteProductMatch.rejected]: (state) => {
      state.deletingMatch = false;
    },
    [searchManualMatch.pending]: (state) => {
      state.searchingManualMatch = true;
    },
    [searchManualMatch.fulfilled]: (state, action) => {
      state.searchingManualMatch = false;
    },
    [searchManualMatch.rejected]: (state) => {
      state.searchingManualMatch = false;
    },
    [updateSellPrice.pending]: (state) => {
      state.updatingSellPrice = true;
    },
    [updateSellPrice.fulfilled]: (state, action) => {
      state.updatingSellPrice = false;
    },
    [updateSellPrice.rejected]: (state) => {
      state.updatingSellPrice = false;
    },
    [checkAsinGatedStatus.pending]: (state) => {
      state.checkingAsinGatedStatus = true;
    },
    [checkAsinGatedStatus.fulfilled]: (state) => {
      state.checkingAsinGatedStatus = false;
    },
    [checkAsinGatedStatus.rejected]: (state) => {
      state.checkingAsinGatedStatus = false;
    },
    [getAsinGatedCount.pending]: (state) => {
      state.fetchingAsinGatedCount = true;
    },
    [getAsinGatedCount.fulfilled]: (state, action) => {
      const { total, willCheck, willSkip, activeSession } = action?.payload || {};
      state.asinGatedCount = { total: total ?? 0, willCheck: willCheck ?? 0, willSkip: willSkip ?? 0 };
      state.fetchingAsinGatedCount = false;
      
      const { context, catalogId } = action.meta.arg;
      const key = `${context}_${catalogId || 'all'}`;
      if (activeSession) {
        state.gatedCheckSessions[key] = activeSession;
      } else {
        delete state.gatedCheckSessions[key];
      }
    },
    [getAsinGatedCount.rejected]: (state) => {
      state.fetchingAsinGatedCount = false;
    },
    [runAllAsinGatedCheck.pending]: (state) => {
      state.runningAllAsinGatedCheck = true;
    },
    [runAllAsinGatedCheck.fulfilled]: (state, action) => {
      state.runningAllAsinGatedCheck = false;
      const { context, catalogId } = action.meta.arg;
      const key = `${context}_${catalogId || 'all'}`;
      if (action.payload?.session) {
        state.gatedCheckSessions[key] = action.payload.session;
      }
    },
    [runAllAsinGatedCheck.rejected]: (state) => {
      state.runningAllAsinGatedCheck = false;
    },
    [getGatedCheckSession.pending]: (state) => {
      state.fetchingGatedCheckSession = true;
    },
    [getGatedCheckSession.fulfilled]: (state, action) => {
      state.fetchingGatedCheckSession = false;
      const session = action.payload?.session;
      if (session) {
        const key = `${session.context}_${session.catalog_id || 'all'}`;
        state.gatedCheckSessions[key] = session;
      }
    },
    [getGatedCheckSession.rejected]: (state) => {
      state.fetchingGatedCheckSession = false;
    },
    [refreshBuyBoxPrice.pending]: (state) => {
      state.refreshingBuyBox = true;
    },
    [refreshBuyBoxPrice.fulfilled]: (state) => {
      state.refreshingBuyBox = false;
    },
    [refreshBuyBoxPrice.rejected]: (state) => {
      state.refreshingBuyBox = false;
    }
  }
});

const { reducer } = catalog;

const { setCatalogStates } = catalog.actions;

export default reducer;

export { setCatalogStates };
